import React, { useContext } from 'react';

import { Typography, List, Button } from 'antd';
import {
  ApplicationUIContext,
  SummaryCard,
  Drawer,
  useDrawerState,
  dateSort,
} from '@yuhuinc/ui-library';

import { useQueryClient } from '@tanstack/react-query';

import { getTranslatedString } from 'helpers/i18n';

import { CurrentApplicationContext } from '../../ApplicationOverview';
import { canRunBackgroundChecksForApplication } from '../../common/helpers';
import RunBackgroundCheck from '../applicants/RunBackgroundCheck';
import { toISO8601DateString } from 'helpers/Formatter';

const { Title } = Typography;

const consentChecksText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.overview.applicants.consentChecks'
);
const creditScoreText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.overview.applicants.creditScore'
);
const datePulledText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.overview.applicants.datePulled'
);
const flagsText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.overview.applicants.flags'
);

const BackgroundChecks = () => {
  const { isMobile } = useContext(ApplicationUIContext);
  const currentApplication = useContext(CurrentApplicationContext);
  const [, addBackgroundCheck] = useDrawerState(false);
  const queryClient = useQueryClient();

  const backgroundCheckRunning = queryClient.isMutating({ mutationKey: ['run_background_check'] });
  const canRunBackgroundChecks = canRunBackgroundChecksForApplication(currentApplication);

  const onRunBackgroundCheckSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['applications'] });
    addBackgroundCheck.close();
  };

  return (
    <>
      {' '}
      <Title level={isMobile ? 4 : 5}>
        {getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.review.backgroundChecks'
        )}
      </Title>
      <List
        grid={{
          gutter: 16,
          column: isMobile ? 1 : 3,
        }}
        dataSource={currentApplication.cotenants}
        renderItem={(cotenant) => {
          const cotenantBackgroundChecks = cotenant.background_check_reports;
          const lastBackgroundCheckPullDate = cotenantBackgroundChecks
            .filter((backgroundCheck) => backgroundCheck.pulled_at)
            .sort((a, b) => dateSort(a.pulled_at, b.pulled_at))
            .reverse()[0]?.pulled_at;
          const cotenantFlagsCount = currentApplication.flags.filter(
            (flag) => flag.cotenant_id === cotenant.id
          ).length;

          return (
            <List.Item>
              <SummaryCard
                heading={cotenant.full_name}
                subheading={getTranslatedString(
                  `landlordPortal.applicationManagement.applicationOverview.overview.applicants.applicantTypes.${cotenant.applicant_type}`
                )}
                additionalText={cotenant.current_address_as_string}
              >
                <SummaryCard.Item
                  label={consentChecksText}
                  buttonText={
                    cotenant.credit_check_consent
                      ? getTranslatedString('yes')
                      : getTranslatedString('no')
                  }
                />
                <SummaryCard.Item label={creditScoreText} buttonText="-" />
                <SummaryCard.Item
                  label={datePulledText}
                  buttonText={
                    lastBackgroundCheckPullDate
                      ? toISO8601DateString(lastBackgroundCheckPullDate)
                      : '-'
                  }
                />
                <SummaryCard.Item label={flagsText} buttonText={cotenantFlagsCount} />
              </SummaryCard>
            </List.Item>
          );
        }}
      />
      <Button
        size={isMobile && 'large'}
        onClick={addBackgroundCheck.open}
        disabled={!canRunBackgroundChecks || backgroundCheckRunning}
        loading={backgroundCheckRunning}
      >
        {getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.review.backgroundChecks.addCheck'
        )}
      </Button>
      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.drawerTitle'
        )}
        visible={addBackgroundCheck.isVisible}
        closable
        destroyOnClose
        onClose={addBackgroundCheck.close}
        width={Drawer.DEFAULT_SIZE}
      >
        <RunBackgroundCheck
          onSuccess={onRunBackgroundCheckSuccess}
          applicants={currentApplication.cotenants.filter(
            (cotenant) => cotenant.lifecycle?.status === 'submitted'
          )}
          creditCheckOptions={currentApplication.application_permissions}
        />
      </Drawer>
    </>
  );
};

export default BackgroundChecks;
